import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { esocialReferenceService, ESocialReferenceItem } from '../../../../../services/esocialReferenceService';

/**
 * Só a Tabela 24 avisa "código não reconhecido": as demais tabelas de referência
 * ainda não foram semeadas, e o aviso acusaria como inválido todo valor já gravado.
 */
const TABELA_AGENTES_NOCIVOS = 24;

interface Props {
  tableNumber: number;
  value: string;
  onChange: (code: string, description: string) => void;
  label: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const ESocialReferenceAutocomplete: React.FC<Props> = ({
  tableNumber, value, onChange, label, required, error, helperText, disabled, size
}) => {
  const [options, setOptions] = useState<ESocialReferenceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [selected, setSelected] = useState<ESocialReferenceItem | null>(null);
  const [unrecognizedCode, setUnrecognizedCode] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const displayedCode = useRef(value);

  /**
   * O código gravado aparece na abertura, sem esperar consulta nenhuma. O que a pessoa
   * digita não muda `value` — só a escolha de uma opção muda, e aí o código já está
   * exibido —, então este efeito só reage a código vindo de fora.
   */
  useEffect(() => {
    if (value === displayedCode.current) return;
    displayedCode.current = value;
    setSelected(null);
    setUnrecognizedCode(null);
    setInputValue(value);
  }, [value]);

  /**
   * A descrição mora na tabela de referência e chega depois. Enquanto não chega, e também
   * quando a tabela não tem o código, o campo segue mostrando o código gravado — nunca
   * vazio. Buscar sem guardar o que já foi buscado é de propósito: em modo estrito o React
   * descarta o resultado da primeira montagem, e um atalho aqui deixaria o campo em branco.
   */
  useEffect(() => {
    if (!value || selected?.code === value) return;

    let atual = true;
    esocialReferenceService.lookup(tableNumber, [value]).then(items => {
      if (!atual) return;
      const achado = items[0];
      if (!achado) {
        setUnrecognizedCode(tableNumber === TABELA_AGENTES_NOCIVOS ? value : null);
        return;
      }
      setSelected(achado);
      setInputValue(`${achado.code} - ${achado.description}`);
      setUnrecognizedCode(null);
    })
      // Sem a consulta não há descrição, e o código gravado já está à vista: nada a fazer.
      .catch(() => undefined);

    return () => { atual = false; };
  }, [value, tableNumber, selected]);

  const handleSearch = (query: string) => {
    if (query.length < 1) { setOptions([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await esocialReferenceService.search(tableNumber, query);
        setOptions(results);
      } catch { setOptions([]); }
      finally { setLoading(false); }
    }, 300);
  };

  return (
    <Autocomplete
      value={selected}
      onChange={(_, val) => {
        displayedCode.current = val?.code ?? '';
        setSelected(val);
        setUnrecognizedCode(null);
        onChange(val?.code ?? '', val?.description ?? '');
      }}
      inputValue={inputValue}
      onInputChange={(_, texto, motivo) => {
        /**
         * Ao sair do campo, e a cada reposição do texto, o MUI reescreve o campo com o
         * rótulo da opção casada — e com nada, quando nenhuma casa. Código gravado que a
         * tabela de referência não reconhece sumiria da tela sem nunca ter saído da ficha.
         */
        if ((motivo === 'blur' || motivo === 'reset') && !selected) {
          setInputValue(value);
          return;
        }
        setInputValue(texto);
        handleSearch(texto);
      }}
      options={options}
      loading={loading}
      disabled={disabled}
      size={size}
      fullWidth
      getOptionLabel={(o) => `${o.code} - ${o.description}`}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      filterOptions={(x) => x}
      noOptionsText={inputValue.length < 1 ? 'Digite para buscar' : 'Nenhum resultado'}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} error={error}
          helperText={unrecognizedCode
            ? `Código "${unrecognizedCode}" não reconhecido na Tabela 24. Selecione o agente correto.`
            : helperText}
          slotProps={{
            formHelperText: unrecognizedCode ? { sx: { color: 'warning.main' } } : undefined,
            input: {
              ...params.InputProps,
              endAdornment: (
                <>{loading && <CircularProgress color="inherit" size={20} />}{params.InputProps.endAdornment}</>
              ),
            }
          }}
        />
      )}
    />
  );
};

export default ESocialReferenceAutocomplete;
